import { isEmpty, isObjectLike, isFinite, includes, filter } from 'lodash';

import {
    COLLECTION_FREQUENCY,
    CALCULATOR_FREQUENCY,
    MODEL_FREQUENCY,
} from 'commons-vehicle/PurchaseMethods/constants/dataConversions.js';
import PaymentFrequencyModel from 'commons-vehicle/PurchaseMethods/models/PaymentFrequencyModel.js';
import {
    WEEKLY,
    BI_WEEKLY,
    SEMI_MONTHLY,
    MONTHLY,
} from 'commons-vehicle/PurchaseMethods/constants/paymentFrequencies.js';

export function resetPaymentFrequencyFromCollectionData(collectionPaymentFrequencyData, cashDown) {
    if (
        isEmpty(collectionPaymentFrequencyData) ||
        !isObjectLike(collectionPaymentFrequencyData) ||
        collectionPaymentFrequencyData.length <= COLLECTION_FREQUENCY.BALLOON_FACTOR
    ) {
        return new PaymentFrequencyModel();
    }

    const updatedCashDown = isFinite(cashDown) ? cashDown : 0;
    const payment =
        collectionPaymentFrequencyData[COLLECTION_FREQUENCY.BASE_PAYMENT] +
        collectionPaymentFrequencyData[COLLECTION_FREQUENCY.FEES_PAYMENT] +
        collectionPaymentFrequencyData[COLLECTION_FREQUENCY.DUTIES_PAYMENT];
    const cashDownAdjustment = collectionPaymentFrequencyData[COLLECTION_FREQUENCY.FINANCE_FACTOR] * updatedCashDown;

    const disclaimers = collectionPaymentFrequencyData[COLLECTION_FREQUENCY.DISCLAIMERS]
        ? collectionPaymentFrequencyData[COLLECTION_FREQUENCY.DISCLAIMERS]
        : [];

    return new PaymentFrequencyModel({
        paymentFrequency: collectionPaymentFrequencyData[COLLECTION_FREQUENCY.PAYMENT_FREQUENCY],
        payment,
        paymentTaxes: collectionPaymentFrequencyData[COLLECTION_FREQUENCY.PAYMENT_TAXES],
        financeFactor: collectionPaymentFrequencyData[COLLECTION_FREQUENCY.FINANCE_FACTOR],
        balloonFactor: collectionPaymentFrequencyData[COLLECTION_FREQUENCY.BALLOON_FACTOR],
        cashDownPaymentAdjustment: cashDownAdjustment,
        netTradeInPaymentAdjustment: 0,
        netTradeInPaymentTaxesAdjustment: 0,
        calculatedNetPayment: payment,
        calculatedPayment: payment - cashDownAdjustment,
        disclaimers,
    });
}

export function resetPaymentFrequencyFromCalculatorData(calculatorPaymentFrequencyData, cashDown) {
    if (isEmpty(calculatorPaymentFrequencyData) || !isObjectLike(calculatorPaymentFrequencyData)) {
        return new PaymentFrequencyModel();
    }

    const frequencyCashDown = calculatorPaymentFrequencyData[CALCULATOR_FREQUENCY.CASH_DOWN];
    const updatedCashDown = isFinite(cashDown) ? cashDown : 0;
    const payment =
        calculatorPaymentFrequencyData[CALCULATOR_FREQUENCY.PAYMENT] +
        calculatorPaymentFrequencyData[CALCULATOR_FREQUENCY.FINANCE_FACTOR] * frequencyCashDown;

    const cashDownAdjustment = calculatorPaymentFrequencyData[CALCULATOR_FREQUENCY.FINANCE_FACTOR] * updatedCashDown;

    const disclaimers = [];
    if (calculatorPaymentFrequencyData[CALCULATOR_FREQUENCY.DISCLAIMER]) {
        disclaimers.push(calculatorPaymentFrequencyData[CALCULATOR_FREQUENCY.DISCLAIMER]);
    }

    return new PaymentFrequencyModel({
        paymentFrequency: calculatorPaymentFrequencyData[CALCULATOR_FREQUENCY.PAYMENT_FREQUENCY],
        payment,
        paymentTaxes: calculatorPaymentFrequencyData[CALCULATOR_FREQUENCY.PAYMENT_TAXES],
        financeFactor: calculatorPaymentFrequencyData[CALCULATOR_FREQUENCY.FINANCE_FACTOR],
        balloonFactor: calculatorPaymentFrequencyData[CALCULATOR_FREQUENCY.BALLOON_FACTOR],
        cashDownPaymentAdjustment: cashDownAdjustment,
        netTradeInPaymentAdjustment: 0,
        netTradeInPaymentTaxesAdjustment: 0,
        calculatedNetPayment: payment,
        calculatedPayment: payment - cashDownAdjustment,
        disclaimers,
    });
}

export function resetPaymentFrequencyFromModelData(modelPaymentFrequencyData, cashDown) {
    if (
        isEmpty(modelPaymentFrequencyData) ||
        !isObjectLike(modelPaymentFrequencyData) ||
        modelPaymentFrequencyData.length <= MODEL_FREQUENCY.BALLOON_FACTOR
    ) {
        return new PaymentFrequencyModel();
    }

    const updatedCashDown = isFinite(cashDown) ? cashDown : 0;
    const payment = modelPaymentFrequencyData[MODEL_FREQUENCY.PAYMENT];
    const cashDownAdjustment = modelPaymentFrequencyData[MODEL_FREQUENCY.FINANCE_FACTOR] * updatedCashDown;

    return new PaymentFrequencyModel({
        paymentFrequency: modelPaymentFrequencyData[MODEL_FREQUENCY.PAYMENT_FREQUENCY],
        payment,
        paymentTaxes: modelPaymentFrequencyData[MODEL_FREQUENCY.PAYMENT_TAXES],
        financeFactor: modelPaymentFrequencyData[MODEL_FREQUENCY.FINANCE_FACTOR],
        balloonFactor: modelPaymentFrequencyData[MODEL_FREQUENCY.BALLOON_FACTOR],
        cashDownPaymentAdjustment: cashDownAdjustment,
        netTradeInPaymentAdjustment: 0,
        netTradeInPaymentTaxesAdjustment: 0,
        calculatedNetPayment: payment,
        calculatedPayment: payment - cashDownAdjustment,
    });
}

// I18n argument is the LocalService class to translate
export function getPaymentFrequencyDropDownValues(I18n, purchaseMethods) {
    let paymentFrequencyDropDownValues = [
        { value: WEEKLY, label: I18n.t('paymentFrequencies.weekly') },
        { value: BI_WEEKLY, label: I18n.t('paymentFrequencies.biWeekly') },
        { value: SEMI_MONTHLY, label: I18n.t('paymentFrequencies.semiMonthly') },
        { value: MONTHLY, label: I18n.t('paymentFrequencies.monthly') },
    ];

    if (purchaseMethods) {
        const availablePaymentFrequencies = purchaseMethods.getFinancesAvailablePaymentFrequencies();
        paymentFrequencyDropDownValues = filter(paymentFrequencyDropDownValues, (paymentFrequencyDropDownValue) =>
            includes(availablePaymentFrequencies, paymentFrequencyDropDownValue.value)
        );
    }

    return paymentFrequencyDropDownValues;
}

export function getPaymentFrequencyDropDownValuesV25(I18n, paymentFrequencies) {
    let paymentFrequencyDropDownValues = [
        { value: WEEKLY, label: I18n.t('paymentFrequencies.weekly') },
        { value: BI_WEEKLY, label: I18n.t('paymentFrequencies.biWeekly') },
        { value: SEMI_MONTHLY, label: I18n.t('paymentFrequencies.semiMonthly') },
        { value: MONTHLY, label: I18n.t('paymentFrequencies.monthly') },
    ];

    if (paymentFrequencies) {
        paymentFrequencyDropDownValues = filter(paymentFrequencyDropDownValues, (paymentFrequencyDropDownValue) =>
            includes(paymentFrequencies, paymentFrequencyDropDownValue.value)
        );
    }

    return paymentFrequencyDropDownValues;
}

export default {
    resetPaymentFrequencyFromCollectionData,
    resetPaymentFrequencyFromCalculatorData,
    resetPaymentFrequencyFromModelData,
    getPaymentFrequencyDropDownValues,
    getPaymentFrequencyDropDownValuesV25,
};
